import { Player } from '../page';

interface ConfigSectionProps {
  players: Player[];
  maxPerList: number;
}

interface CategoryStats {
  name: string;
  individuales: number;
  duplas: number;
  trios: number;
  total: number;
}

export default function ConfigSection({ players, maxPerList }: ConfigSectionProps) {
  const clubs = [...new Set(players.map(p => p.club))].filter(Boolean);
  const categories = [...new Set(players.map(p => p.categoria))].filter(Boolean);

  // Clasificar cada fila según los jugadores que tenga
  const getModalidad = (p: Player): 'individual' | 'dupla' | 'trio' => {
    const j3 = p.jugador3?.trim();
    const j2 = p.jugador2?.trim();
    if (j3) return 'trio';
    if (j2) return 'dupla';
    return 'individual';
  };

  const categoryStats: CategoryStats[] = categories.map((cat) => {
    const catPlayers = players.filter(p => p.categoria === cat);
    const individuales = catPlayers.filter(p => getModalidad(p) === 'individual').length;
    const duplas = catPlayers.filter(p => getModalidad(p) === 'dupla').length;
    const trios = catPlayers.filter(p => getModalidad(p) === 'trio').length;
    return { name: cat, individuales, duplas, trios, total: catPlayers.length };
  });

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
          <i className="ri-settings-3-line text-xl text-white"></i>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Configuración de Listas</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm">
              <i className="ri-team-line text-lg text-indigo-600"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-700">Total Jugadores</h3>
          </div>
          <p className="text-3xl font-bold text-indigo-600">{players.length}</p>
        </div>

        <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm">
              <i className="ri-building-line text-lg text-emerald-600"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-700">Clubes</h3>
          </div>
          <p className="text-3xl font-bold text-emerald-600">{clubs.length}</p>
        </div>

        <div className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg shadow-sm">
              <i className="ri-list-check text-lg text-amber-600"></i>
            </div>
            <h3 className="text-sm font-semibold text-gray-700">Máximo por Lista</h3>
          </div>
          <p className="text-3xl font-bold text-amber-600">{maxPerList}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Clubes Registrados */}
        <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <i className="ri-building-line text-base text-gray-600"></i>
            Clubes Registrados
          </h4>
          <div className="flex flex-wrap gap-2">
            {clubs.map((club, index) => (
              <span
                key={index}
                className="px-3 py-1.5 bg-white text-xs font-medium text-gray-700 rounded-full border border-gray-200"
              >
                {club}
              </span>
            ))}
          </div>
        </div>

        {/* Categorías con detalle */}
        <div className="p-5 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <i className="ri-trophy-line text-base text-gray-600"></i>
            Categorías
          </h4>
          <div className="space-y-3">
            {categoryStats.map((cat, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-800">{cat.name}</span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {cat.total} filas
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {cat.individuales > 0 && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full border border-indigo-100">
                      <i className="ri-user-line text-xs"></i>
                      {cat.individuales} individual{cat.individuales !== 1 ? 'es' : ''}
                    </span>
                  )}
                  {cat.duplas > 0 && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full border border-emerald-100">
                      <i className="ri-group-line text-xs"></i>
                      {cat.duplas} dupla{cat.duplas !== 1 ? 's' : ''}
                    </span>
                  )}
                  {cat.trios > 0 && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-100">
                      <i className="ri-team-line text-xs"></i>
                      {cat.trios} trío{cat.trios !== 1 ? 's' : ''}
                    </span>
                  )}
                  {cat.individuales === 0 && cat.duplas === 0 && cat.trios === 0 && (
                    <span className="text-xs text-gray-400 italic">Sin datos de modalidad</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}